/**
 * Chat scrolling exactly like the chat modal (chatModal.js, recalcSpacer /
 * updateScrollDownVisibility / .scroll-to-bottom-btn):
 * - when a new message of the user appears, it is moved to the TOP of the list
 *   (a spacer below the messages makes that possible) and the answer is written
 *   underneath it;
 * - while the answer grows, the list does NOT follow it; once the answer reaches
 *   the bottom edge, the round arrow button appears (centred at the bottom);
 * - the spacer shrinks as the answer grows, without ever moving the view;
 * - opening a conversation shows its end;
 * - `calm` (AI Studio's start page): a message sent from it is pinned at once instead of
 *   scrolled to, so the welcome above simply gives way and only the input moves;
 * - the view holds: when something below the list changes its height (the input shrinking
 *   after the start page, the focus point above the input coming or going), the list is
 *   put back where it was before the frame is drawn, so the browser cannot pull it down.
 * User messages are found by the `.user-message` class (as in the chat modal).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TOP_GAP = 8; // breathing room above the pinned user message

export function useChatScroll(convKey, deps, calm = false) {
  const ref = useRef(null);
  const calmBefore = useRef(calm); // as it was before this render's change
  const spacerRef = useRef(null);
  // top: the scroll position to hold (the owner's own or the one set here)
  const st = useRef({ key: null, userCount: 0, active: false, programmatic: false, top: 0 });
  const [show, setShow] = useState(false);

  const pin = useRef({ timer: 0 });

  useEffect(() => () => clearTimeout(pin.current.timer), []);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (st.current.programmatic) { setShow(false); return; }
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShow(distance >= 40 && el.scrollHeight > el.clientHeight);
  }, []);

  // back to the position to hold if the list got pulled down (never further than it can go)
  const hold = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (el.scrollTop < st.current.top - 1) el.scrollTop = Math.min(st.current.top, el.scrollHeight - el.clientHeight);
  }, []);

  const onScroll = useCallback(() => {
    if (ref.current) st.current.top = ref.current.scrollTop;
    update();
  }, [update]);

  const lastUserMessage = () => {
    const users = ref.current ? ref.current.querySelectorAll('.user-message') : [];
    return users.length ? users[users.length - 1] : null;
  };

  const sizeSpacer = useCallback(() => {
    const el = ref.current;
    const sp = spacerRef.current;
    if (!el || !sp) return;
    const users = el.querySelectorAll('.user-message');
    const msg = users[users.length - 1];
    if (!msg) {
      sp.style.height = '0px';
      st.current.active = false;
      return;
    }
    const style = getComputedStyle(el);
    if (msg.offsetTop - (parseFloat(style.paddingTop) || 0) <= TOP_GAP) {
      // the message is the first thing in the list, so it is at the top already: nothing to pin,
      // and a spacer would only create a few pixels of scroll range (a needless scrollbar)
      sp.style.height = '0px';
      st.current.active = false;
      return;
    }
    const padBottom = parseFloat(style.paddingBottom) || 0;
    const below = sp.offsetTop - msg.offsetTop;
    const need = Math.max(0, el.clientHeight - below - TOP_GAP - padBottom);
    sp.style.height = `${need}px`;
    st.current.active = need > 0;
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !convKey) return;
    const s = st.current;
    const count = el.querySelectorAll('.user-message').length;
    if (s.key !== convKey) {
      // conversation opened: show its end
      s.key = convKey;
      s.userCount = count;
      s.programmatic = false;
      if (spacerRef.current) spacerRef.current.style.height = '0px';
      s.active = false;
      el.scrollTop = el.scrollHeight;
      s.top = el.scrollTop;
      update();
    } else if (count > s.userCount) {
      // the user just sent a message: pin it to the top, the answer comes below
      // (runs outside this effect's lifecycle: the streaming answer re-runs the
      // effect many times and must not cancel the scroll)
      s.userCount = count;
      sizeSpacer();
      clearTimeout(pin.current.timer);
      const target = () => {
        const msg = lastUserMessage();
        if (!msg) return el.scrollHeight;
        const fromTop = msg.offsetTop - (parseFloat(getComputedStyle(el).paddingTop) || 0);
        return fromTop <= TOP_GAP ? 0 : msg.offsetTop - TOP_GAP; // first in the list: stays where it is
      };
      if (calmBefore.current) {
        // from the start page: the message is at the top at once (the DOM is current here,
        // in a layout effect); from now on the view holds while the input shrinks
        el.scrollTop = target();
        s.top = el.scrollTop;
        s.programmatic = false;
        update();
        return;
      }
      s.programmatic = true;
      setShow(false);
      el.scrollTo({ top: target(), behavior: 'smooth' });
      pin.current.timer = setTimeout(() => {
        // smooth scrolling may not finish (e.g. a background tab): settle there
        sizeSpacer();
        const top = Math.min(target(), el.scrollHeight - el.clientHeight);
        if (Math.abs(el.scrollTop - top) > 2) el.scrollTop = top;
        s.top = el.scrollTop;
        s.programmatic = false;
        update();
      }, 700);
    } else {
      s.userCount = count;
      if (s.active) {
        sizeSpacer(); // the answer grew: shrink the spacer, never move the view
        if (!s.programmatic) hold();
      }
      update();
    }
  }, [convKey, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => { calmBefore.current = calm; }); // after the effect above

  // the list itself changed size (the input or the focus point above it): resize the spacer
  // and hold the view; runs after layout and before the frame is drawn
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const s = st.current;
      if (!s.key || s.programmatic) return;
      if (s.active) sizeSpacer();
      hold();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [sizeSpacer, hold]);

  const scrollToBottom = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setShow(false);
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    setTimeout(() => {
      if (el.scrollHeight - el.scrollTop - el.clientHeight > 2) el.scrollTop = el.scrollHeight;
      st.current.top = el.scrollTop;
      update();
    }, 700);
  }, [update]);

  return { ref, spacerRef, onScroll, show, scrollToBottom };
}

export function ScrollDownButton({ show, onClick }) {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <button type="button" className="as-scroll-down" onClick={() => onClick()} aria-label={t('chat_scroll_latest_aria')}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="14" height="14" aria-hidden="true">
        <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8V64c0-17.7-14.3-32-32-32s-32 14.3-32 32v306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" fill="currentColor" />
      </svg>
    </button>
  );
}
